import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  buildInitialMergeState,
  fetchMergePage,
  PAGE_SIZE,
  type DatedRow,
  type MergeState,
} from "@/lib/utils/history-merge-pagination";
import { getEnabledSourcesForPatientFilters } from "@/lib/utils/history-source-filter";
import { REALIZED_STAY_DAY_STATUSES } from "@/lib/daycare-hotel/stayDays";

const HISTORY_STAY_DAY_STATUSES = [...REALIZED_STAY_DAY_STATUSES, "no_show"];

// The individual source rows are intentionally untyped here (matching this
// component's pre-existing multi-pet fetch, which also returned loosely
// typed rows): PatientHistoryTimeline.tsx casts each array to its specific
// row type where it needs strong typing (e.g. `medicalTimelineRecords`).
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface PatientHistoryFeedPage {
  pets: Array<{ id: string; name: string | null }>;
  medicalRecords: any[];
  vaccinations: any[];
  attachments: any[];
  prescriptions: any[];
  terms: any[];
  grooming: any[];
  appointments: any[];
  stayDays: StayHistoryRow[];
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface StayHistoryRow {
  id: string;
  petId: string;
  stayDate: string;
  checkInDate: string;
  checkOutDate: string | null;
  status: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  moduleType: string;
  isOvernight?: boolean;
  reservationId: string;
  bedLabel: string | null;
  /** Nome do plano que debitou esta diária no check-in; `null` se foi avulso ou faltou. */
  planName: string | null;
  planUnits: number | null;
}

interface RawStayDayRow {
  id: string;
  pet_id: string;
  stay_date: string;
  status: string;
  check_in_time: string | null;
  check_out_time: string | null;
  stay_reservation_id: string;
  stay_reservations: {
    module_type: string;
    is_overnight: boolean;
    check_in_date: string;
    check_out_date: string | null;
    stay_reservation_recurrence: Array<{ ends_on: string | null }> | null;
    daycare_plan_usages: Array<{ units: number; source: string; used_on: string; contract: { terms_snapshot: { name?: string | null } | null } | null }> | null;
    hotel_plan_usages: Array<{ units: number; source: string; used_on: string; contract: { terms_snapshot: { name?: string | null } | null } | null }> | null;
  } | null;
  hotel_room_beds: { name: string; hotel_rooms: { name: string } | null } | null;
}

const mapStayDayRow = (row: RawStayDayRow): StayHistoryRow => {
  const usage = [
    ...(row.stay_reservations?.daycare_plan_usages ?? []),
    ...(row.stay_reservations?.hotel_plan_usages ?? []),
  ].find((item) => item.source === "checkin" && item.used_on === row.stay_date);

  return {
    id: row.id,
    petId: row.pet_id,
    stayDate: row.stay_date,
    checkInDate: row.stay_reservations?.check_in_date ?? row.stay_date,
    checkOutDate:
      row.stay_reservations?.check_out_date ??
      row.stay_reservations?.stay_reservation_recurrence?.[0]?.ends_on ??
      null,
    status: row.status,
    checkInTime: row.check_in_time,
    checkOutTime: row.check_out_time,
    moduleType: row.stay_reservations?.module_type ?? "daycare",
    isOvernight: row.stay_reservations?.is_overnight ?? false,
    reservationId: row.stay_reservation_id,
    bedLabel: row.hotel_room_beds
      ? row.hotel_room_beds.hotel_rooms?.name
        ? `${row.hotel_room_beds.hotel_rooms.name} · ${row.hotel_room_beds.name}`
        : row.hotel_room_beds.name
      : null,
    planName: usage?.contract?.terms_snapshot?.name ?? null,
    planUnits: usage?.units ?? null,
  };
};

export interface UsePatientHistoryFeedParams {
  resolvedPetIds: string[];
  historyClinicIds: string[] | null;
  includeGrooming: boolean;
  groomingTypeIds: string[];
  selectedFilters: string[];
}

interface InternalPatientHistoryFeedPage extends PatientHistoryFeedPage {
  __mergeState: MergeState;
  __hasNextPage: boolean;
}

export const usePatientHistoryFeed = ({
  resolvedPetIds,
  historyClinicIds,
  includeGrooming,
  groomingTypeIds,
  selectedFilters,
}: UsePatientHistoryFeedParams) => {
  const enabledSources = getEnabledSourcesForPatientFilters(selectedFilters);

  return useInfiniteQuery({
    queryKey: [
      "patient-history-feed",
      resolvedPetIds,
      historyClinicIds,
      includeGrooming,
      groomingTypeIds,
      selectedFilters,
    ],
    enabled: resolvedPetIds.length > 0,
    initialPageParam: null as MergeState | null,
    getNextPageParam: (lastPage: InternalPatientHistoryFeedPage) =>
      lastPage.__hasNextPage ? lastPage.__mergeState : undefined,
    queryFn: async ({ pageParam }): Promise<InternalPatientHistoryFeedPage> => {
      if (resolvedPetIds.length === 0) {
        return {
          pets: [],
          medicalRecords: [],
          vaccinations: [],
          attachments: [],
          prescriptions: [],
          terms: [],
          grooming: [],
          appointments: [],
          stayDays: [],
          __mergeState: { sources: {} },
          __hasNextPage: false,
        };
      }

      const countFns: Record<string, () => Promise<number>> = {};
      const fetchFns: Record<string, (cursor: string | null, limit: number) => Promise<DatedRow[]>> = {};

      if (enabledSources.consultation) {
        countFns.medicalRecords = async () => {
          const { count, error } = await supabase
            .from("medical_records")
            .select("*", { count: "exact", head: true })
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds);
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.medicalRecords = async (cursor, limit) => {
          let query = supabase
            .from("medical_records")
            .select(
              `
              *,
              pets ( id, name, owners ( id, name ) ),
              profiles!fk_vet_user ( id, full_name ),
              clinics ( id, name )
            `,
            )
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds)
            .order("consultation_date", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("consultation_date", cursor);
          const { data, error } = await query;
          if (error) throw error;
          return (data ?? []).map((row) => ({
            date: row.consultation_date,
            sourceKey: "medicalRecords",
            raw: row,
          }));
        };
      }

      if (enabledSources.vaccine) {
        countFns.vaccinations = async () => {
          const { count, error } = await supabase
            .from("pet_applications")
            .select("*", { count: "exact", head: true })
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds);
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.vaccinations = async (cursor, limit) => {
          let query = supabase
            .from("pet_applications")
            .select(
              `
              *,
              item_id:items!pet_applications_item_id_fkey(id, name, description, product_subtype:product_subtypes(name))
            `,
            )
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds)
            .order("application_date", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("application_date", cursor);
          const { data, error } = await query;
          if (error) throw error;
          return (data ?? []).map((row) => ({
            date: row.application_date,
            sourceKey: "vaccinations",
            raw: row,
          }));
        };
      }

      if (enabledSources.exam) {
        countFns.attachments = async () => {
          const { count, error } = await supabase
            .from("pet_attachments")
            .select("*", { count: "exact", head: true })
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds);
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.attachments = async (cursor, limit) => {
          let query = supabase
            .from("pet_attachments")
            .select(
              `
              *,
              clinics ( id, name )
            `,
            )
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds)
            .order("created_at", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("created_at", cursor);
          const { data, error } = await query;
          if (error) throw error;
          return (data ?? []).map((row) => ({ date: row.created_at, sourceKey: "attachments", raw: row }));
        };
      }

      if (enabledSources.prescription) {
        countFns.prescriptions = async () => {
          const { count, error } = await supabase
            .from("medical_prescriptions")
            .select("*", { count: "exact", head: true })
            .in("pet_id", resolvedPetIds);
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.prescriptions = async (cursor, limit) => {
          let query = supabase
            .from("medical_prescriptions")
            .select(
              `
              *,
              items:medical_prescription_items(*),
              exam_items:medical_prescription_exam_items(*),
              vet:profiles!medical_prescriptions_vet_user_id_fkey(id, full_name, address),
              clinic:clinics(id, name)
            `,
            )
            .in("pet_id", resolvedPetIds)
            .order("issued_at", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("issued_at", cursor);
          const { data, error } = await query;
          if (error) throw error;
          return (data ?? []).map((row) => ({ date: row.issued_at, sourceKey: "prescriptions", raw: row }));
        };
      }

      if (enabledSources.term && historyClinicIds && historyClinicIds.length > 0) {
        countFns.terms = async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count, error } = await (supabase as any)
            .from("patient_terms")
            .select("*", { count: "exact", head: true })
            .in("clinic_id", historyClinicIds)
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds);
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.terms = async (cursor, limit) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let query = (supabase as any)
            .from("patient_terms")
            .select("*")
            .in("clinic_id", historyClinicIds)
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds)
            .order("created_at", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("created_at", cursor);
          const { data, error } = await query;
          if (error) throw error;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (data ?? []).map((row: any) => ({ date: row.created_at, sourceKey: "terms", raw: row }));
        };
      }

      if (includeGrooming && enabledSources.grooming && groomingTypeIds.length > 0) {
        countFns.grooming = async () => {
          const { count, error } = await supabase
            .from("appointments")
            .select("*", { count: "exact", head: true })
            .in("type", groomingTypeIds)
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds);
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.grooming = async (cursor, limit) => {
          let query = supabase
            .from("appointments")
            .select(
              `
              *,
              clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
              appointment_items(
                id,
                title_snapshot,
                item:items(name)
              )
            `,
            )
            .in("type", groomingTypeIds)
            .is("deleted_at", null)
            .in("pet_id", resolvedPetIds)
            .order("starts_at", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("starts_at", cursor);
          const { data, error } = await query;
          if (error) throw error;
          return (data ?? []).map((row) => ({ date: row.starts_at, sourceKey: "grooming", raw: row }));
        };
      }

      if (enabledSources.stay) {
        countFns.stayDays = async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count, error } = await (supabase as any)
            .from("stay_reservation_days")
            .select("id, stay_reservations!inner ( module_type )", { count: "exact", head: true })
            .in("pet_id", resolvedPetIds)
            .in("status", HISTORY_STAY_DAY_STATUSES)
            .lte("stay_date", new Date().toLocaleDateString("en-CA"));
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.stayDays = async (cursor, limit) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let query = (supabase as any)
            .from("stay_reservation_days")
            .select(
              `id, pet_id, stay_date, status, check_in_time, check_out_time, stay_reservation_id,
               stay_reservations!inner (
                 module_type, is_overnight, check_in_date, check_out_date,
                 stay_reservation_recurrence ( ends_on ),
                 daycare_plan_usages:stay_reservation_daycare_plan_usages ( units, source, used_on, contract:daycare_plan_contracts ( terms_snapshot ) ),
                 hotel_plan_usages:stay_reservation_hotel_plan_usages ( units, source, used_on, contract:hotel_plan_contracts ( terms_snapshot ) )
               ),
               hotel_room_beds ( name, hotel_rooms ( name ) )`,
            )
            .in("pet_id", resolvedPetIds)
            .in("status", HISTORY_STAY_DAY_STATUSES)
            .lte("stay_date", new Date().toLocaleDateString("en-CA"))
            .order("stay_date", { ascending: false })
            .limit(limit);
          if (cursor) query = query.lt("stay_date", cursor);
          const { data, error } = await query;
          if (error) throw error;
          return ((data ?? []) as RawStayDayRow[]).map((row) => ({
            date: row.stay_date,
            sourceKey: "stayDays",
            raw: row,
          }));
        };
      }

      if (enabledSources.appointment) {
        countFns.appointments = async () => {
          let query = supabase
            .from("appointments")
            .select("id, owner:owners!inner(id)", { count: "exact", head: true })
            .in("pet_id", resolvedPetIds)
            .is("deleted_at", null)
            .is("owner.deleted_at", null)
            .lte("starts_at", new Date().toISOString());
          if (groomingTypeIds.length > 0) query = query.not("type", "in", `(${groomingTypeIds.join(",")})`);
          if (historyClinicIds && historyClinicIds.length > 0) query = query.in("clinic_id", historyClinicIds);
          const { count, error } = await query;
          if (error) throw error;
          return count ?? 0;
        };
        fetchFns.appointments = async (cursor, limit) => {
          let query = supabase
            .from("appointments")
            .select(
              `
              id, clinic_id, vet_user_id, owner_id, pet_id, type, status, starts_at, ends_at, notes,
              created_by_user_id, created_at, updated_at,
              clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
              pet:pets!appointments_pet_id_fkey(id, name, deleted_at),
              owner:owners!inner(id, name, deleted_at),
              vet:profiles!appointments_vet_user_id_fkey(id, full_name),
              appointment_pets( pet:pets(id, name, deleted_at) ),
              appointment_items( id, appointment_id, item_id, qty, title_snapshot, item:items(id, name, type, sale_price) )
            `,
            )
            .in("pet_id", resolvedPetIds)
            .is("deleted_at", null)
            .is("owner.deleted_at", null)
            .order("starts_at", { ascending: false })
            .limit(limit);
          query = cursor ? query.lt("starts_at", cursor) : query.lte("starts_at", new Date().toISOString());
          if (groomingTypeIds.length > 0) query = query.not("type", "in", `(${groomingTypeIds.join(",")})`);
          if (historyClinicIds && historyClinicIds.length > 0) query = query.in("clinic_id", historyClinicIds);
          const { data, error } = await query;
          if (error) throw error;
          return (data ?? []).map((row) => ({ date: row.starts_at, sourceKey: "appointments", raw: row }));
        };
      }

      const [petsResult, state] = await Promise.all([
        supabase.from("pets").select("id, name").in("id", resolvedPetIds),
        pageParam ?? buildInitialMergeState(countFns),
      ]);
      if (petsResult.error) throw petsResult.error;

      const { items, nextState, hasNextPage } = await fetchMergePage(state, fetchFns, PAGE_SIZE);

      const bySource: Record<string, unknown[]> = {};
      items.forEach((item) => {
        (bySource[item.sourceKey] ??= []).push(item.raw);
      });

      return {
        pets: petsResult.data ?? [],
        medicalRecords: bySource.medicalRecords ?? [],
        vaccinations: bySource.vaccinations ?? [],
        attachments: bySource.attachments ?? [],
        prescriptions: bySource.prescriptions ?? [],
        terms: bySource.terms ?? [],
        grooming: bySource.grooming ?? [],
        appointments: bySource.appointments ?? [],
        stayDays: ((bySource.stayDays ?? []) as RawStayDayRow[]).map(mapStayDayRow),
        __mergeState: nextState,
        __hasNextPage: hasNextPage,
      };
    },
  });
};
